import React, { useState } from "react";
import { Form, Button } from "reactstrap";
import gql from "graphql-tag";
import styled from "styled-components";
import { useMutation } from "@apollo/client";
import { store } from "react-notifications-component";
import { formatTeamReward } from "../../../../utilities/utils";
import Loading, { Error } from "../../../../components/Loading";
import WeekDaySelector from "../../../../components/WeekDaySelector";
import EnumSelector from "../../../../components/EnumSelector";
import {
  WEEKDAY_INTERACTIONS_QUERY,
  MEMBER_LOAD_QUERY
} from "../../../../graphql/queries";

const UPSERT_INTERACTION_MUTATION = gql`
  mutation UpsertTeamReward(
    $id: Int
    $teamId: Int!
    $sprintId: Int!
    $weekNumber: Int!
    $weekDay: Weekday!
    $rewardKind: TeamRewardKind!
  ) {
    upsertTeamReward(
      id: $id
      teamId: $teamId
      sprintId: $sprintId
      weekNumber: $weekNumber
      weekDay: $weekDay
      rewardKind: $rewardKind
    ) {
      id
    }
  }
`;

const StyledForm = styled(Form)`
  display: grid;
  grid-template-columns: 200px 200px;
  grid-gap: 10px;

  > .last {
    grid-column: 1 / -1;
  }
`;

const TeamRewardForm = ({
  toggleModal,
  teamId,
  weekDay,
  type,
  sprintId,
  weekNumber,
  data
}) => {
  const [form, setValues] = useState({
    id: data ? data.id : null,
    teamId,
    sprintId,
    weekNumber,
    weekDay,
    type,
    rewardKind: data ? data.teamRewardDetail.rewardKind : null
  });

  const queriesToRefetch = [
    {
      query: WEEKDAY_INTERACTIONS_QUERY,
      variables: { teamId, sprintId, weekNumber }
    },
    {
      query: MEMBER_LOAD_QUERY,
      variables: { teamId, sprintId }
    }
  ];

  const [
    upsertInteraction,
    { loading: mutationLoading, error: mutationError }
  ] = useMutation(UPSERT_INTERACTION_MUTATION, {
    refetchQueries: queriesToRefetch
  });

  if (mutationError) return <Error error={mutationError} />;

  const onChangeOption = (value, action) => {
    setValues({
      ...form,
      [action.name]: value.value
    });
  };

  const onSubmitHandler = e => {
    e.preventDefault();

    if (form.weekDay === null) {
      showNotification("Please select a day of the week");
      return;
    }

    if (form.rewardKind === null) {
      showNotification("Please select an option of reward");
      return;
    }

    upsertInteraction({ variables: { ...form } }).then(() => {
      toggleModal();
    });
  };

  const showNotification = text => {
    store.addNotification({
      message: text,
      container: "top-right",
      type: "warning",
      animationIn: ["animated", "bounceIn"],
      animationOut: ["animated", "bounceOut"],
      dismiss: { duration: 5000 },
      width: 294,
      content: <div className="notification-sim">{text}</div>
    });
  };

  return (
    <StyledForm id="teamReward-form" onSubmit={onSubmitHandler}>
      <WeekDaySelector
        controlName="weekDay"
        defaultValue={weekDay}
        onChange={onChangeOption}
      />

      <EnumSelector
        controlName="rewardKind"
        enumType="TeamRewardKind"
        title="Reward"
        formatProvider={formatTeamReward}
        defaultValue={form.rewardKind}
        onChange={onChangeOption}
      />

      <Button
        disabled={
          mutationLoading ||
          (data && Object.values(form).some(value => value === null))
        }
        className="last"
        color="primary"
        type="submit"
      >
        {mutationLoading ? (
          <Loading text={data ? "Updating" : "Saving"} />
        ) : data ? (
          "Update"
        ) : (
          "Save"
        )}
      </Button>
    </StyledForm>
  );
};

export default TeamRewardForm;
